<template>
  <transition name="fade">
    <div
      v-show="dialogVisible"
      @click.self="closeDialog"
      class="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-black bg-opacity-60 dark:bg-opacity-80 backdrop-blur-sm"
      :class="{
        'pointer-events-auto': dialogVisible,
        'pointer-events-none': !dialogVisible,
      }"
    >
      <transition :name="animation" @after-enter="focusFirstElement">
        <div
          v-show="dialogVisible"
          class="relative w-full max-w-3xl rounded-lg text-blue-gray-500 dark:text-gray-300 shadow-2xl"
          :class="[
            modalSizeClass,
            {
              'overflow-y-auto': !fullScreen,
              'h-[calc(100vh-2rem)]': fullScreen,
              'landscape:h-[80vh] portrait:h-[90vh]': !fullScreen,
            },
          ]"
        >
          <slot name="modalContent"></slot>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
export default {
  name: "ResponsiveModal",
  props: {
    dialogVisible: {
      type: Boolean,
      default: false,
    },
    width: {
      type: String,
      default: "normal", // 'narrow', 'normal', 'wide', 'full'
    },
    height: {
      type: String,
      default: "auto", // 'auto', 'full', 'screen'
    },
    animation: {
      type: String,
      default: "fade", // 'fade', 'slide-up', 'slide-down'
      validator: (value) => ["fade", "slide-up", "slide-down"].includes(value),
    },
  },
  computed: {
    fullScreen() {
      return this.width === "full" || this.height === "screen";
    },
    modalSizeClass() {
      const widthClasses = {
        narrow: "max-w-md",
        normal: "max-w-3xl",
        wide: "max-w-5xl",
        full: "w-full max-w-full mx-0 rounded-none",
      };
      return widthClasses[this.width];
    },
  },
  methods: {
    closeDialog() {
      this.$emit("update:dialogVisible", false);
    },
    handleEscapeKey(event) {
      if (event.key === "Escape" && this.dialogVisible) {
        this.closeDialog();
      }
    },
    focusFirstElement() {
      const focusableElements = this.$el.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    },
  },
  watch: {
    dialogVisible(newVal) {
      if (newVal) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
    },
  },
  mounted() {
    window.addEventListener("keydown", this.handleEscapeKey);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.handleEscapeKey);
    document.body.style.overflow = "";
  },
};
</script>

<style scoped>
/* Transition animations */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease-out;
}
.slide-up-enter-from {
  opacity: 0;
  transform: translateY(20px);
}
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease-out;
}
.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Responsive adjustments */
@media (orientation: landscape) {
  .landscape\:h-\[80vh\] {
    height: 80vh;
  }
}

@media (orientation: portrait) {
  .portrait\:h-\[90vh\] {
    height: 90vh;
  }
}
</style>
